import { useLayoutEffect, useRef, useState } from 'preact/hooks';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { cloneSafe } from 'Utils/components';

import PaginationButton, { type PaginationButtonTemplateParams } from './paginationButton';

const itemHeight = 30; // props? options?
const viewportHeight = 200;
const visibleItemsCount = Math.trunc(viewportHeight / itemHeight);

interface Props {
  template: TemplateFunction<PaginationDropdownTemplateParams>;
  pages: PaginationDropdownPageProps[];
}

// The dropdown only ever receives `type: 'page'` entries (see
// paginationMenu.tsx: pages are built from `pageProps`). The narrower
// shape keeps required fields required.
export type PaginationDropdownPageProps = {
  hideDropdown?: () => void;
  isActive?: boolean;
  pageNumber: number;
  searchParams?: string;
  type: 'page';
  totalPages: number;
};

export interface PaginationDropdownTemplateParams {
  onViewportScroll: () => void;
  pages: TemplateFunctionInvoker<PaginationButtonTemplateParams>[];
}

export default function PaginationDropdown({ template, pages }: Props): TemplateResult {
  const rootRef = useRef<HTMLDivElement>(null);

  const [currentSlot, setCurrentSlot] = useState({
    start: 0,
    end: visibleItemsCount,
  });

  const containerStyle = { height: pages.length * itemHeight };

  useLayoutEffect(function () {
    const root = rootRef.current;
    const elem = root?.querySelector('.cm_scroll-container');
    if (elem instanceof HTMLElement) {
      elem.style.height = `${containerStyle.height}px`;
    }
  });

  const onViewportScroll = () => {
    const root = rootRef.current;
    const viewport = root?.querySelector('.cm_scroll-viewport');
    if (viewport instanceof HTMLElement) {
      const zeroBound = Math.trunc(viewport.scrollTop / itemHeight);
      const topBound = zeroBound - visibleItemsCount;
      const currentStart = topBound >= pages.length ? topBound : zeroBound;
      if (currentStart !== currentSlot.start) {
        const bottomBound = currentStart + visibleItemsCount;
        setCurrentSlot({
          start: currentStart,
          end: bottomBound >= pages.length ? pages.length - 1 : bottomBound,
        });
      }
    }
  };

  const visiblePages: TemplateFunctionInvoker<PaginationButtonTemplateParams>[] = [];
  for (let i = currentSlot.start; i <= currentSlot.end; i++) {
    if (typeof pages[i] === 'undefined') {
      break;
    }
    visiblePages.push((templ) => (
      <PaginationButton
        template={templ}
        style={{ top: i * itemHeight, height: itemHeight }}
        {...pages[i]}
        key={`page-btn-${pages[i].type}-${pages[i].pageNumber}`}
      />
    ));
  }

  const component = template.call({ pages: visiblePages, onViewportScroll });

  return cloneSafe(component, rootRef);
}
